import {CurrentPageStore} from '@/store/content-optimizer/currentPage';
import {formatLargeNumber} from '@/utils/number';
import {LoadingOutlined} from '@ant-design/icons';
import {faChevronDown, faChevronUp} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Spin, Tooltip, Input as AntdInput} from 'antd';
import {toJS} from 'mobx';
import {observer} from 'mobx-react-lite';
import {ChangeEvent, useState} from 'react';
import styled from 'styled-components';
import {faMagnifyingGlass} from '@fortawesome/pro-solid-svg-icons';

interface Props {
  store: CurrentPageStore;
  onFilter: (query: string[]) => void;
}
export const TermsDataController = observer(({
  store,
  onFilter,
}: Props) => {
  const [isClustersExpanded, setIsClustersExpanded] = useState<boolean>(false);
  const [termsSearchQuery, setTermsSearchQuery] = useState<string[]>([]);
  const [termsSearchInputValue, setTermsSearchInputValue] = useState<string>('');

  const secondAntIcon = <LoadingOutlined style={{fontSize: '12px important', color: '#7f4ead'}} spin />;


  const handleOnClusterClick = (clusterName: string) => {
    let filteredSearch = termsSearchInputValue[0] ? termsSearchInputValue.split(',')?.map(item => item.trim()) : [];

    if (filteredSearch?.includes(clusterName)) {
      filteredSearch = filteredSearch.filter(item => item !== clusterName);
    } else {
      filteredSearch.push(clusterName);
    }
    setTermsSearchInputValue(filteredSearch.join(', '));

    onFilter(filteredSearch);
  };

  const termSearchHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    setTermsSearchInputValue(input);

    const formatInput = input.split(',');
    const trimmedArr: string[] = [];
    for (let i = 0; i < formatInput.length; i++) {
      if (!termsSearchQuery.includes(formatInput[i].trim())) {
        trimmedArr.push(formatInput[i].trim());
      }
    }

    setTermsSearchQuery(trimmedArr);
    onFilter(trimmedArr);
  };

  return <Wrapper>
    <SearchAndSort>
      <InputStyled placeholder='Search terms' prefix={
        <FontAwesomeIcon icon={faMagnifyingGlass} fontSize={14} color='#A3A4A4' />
      } value={termsSearchInputValue} onChange={e => termSearchHandler(e)} />
    </SearchAndSort>
    <ClustersWrapper>
      {store?.isAnalyzing ? <Spin indicator={secondAntIcon}/> : <ClustersContainer expandedClusters={isClustersExpanded}>
        {toJS(store.analytics?.clusters)?.map((cluster, id) => {
          return <Tooltip placement='bottom'
            key={id}
            overlayInnerStyle={{background: '#000', width: 200, borderRadius: 5}}
            title={<div>
              <ClusterTooltipSingleRow>
                <ClusterTooltipLabel>Terms in cluster:</ClusterTooltipLabel>
                <ClusterTooltipValue>{cluster.focusTerms?.length}</ClusterTooltipValue>
              </ClusterTooltipSingleRow>
              <ClusterTooltipSingleRow>
                <ClusterTooltipLabel>Monthly Search Volume:</ClusterTooltipLabel>
                <ClusterTooltipValue>{formatLargeNumber(cluster.clusterSv)}</ClusterTooltipValue>
              </ClusterTooltipSingleRow>
            </div>}>
            <SingleClusterContainer activeCluster={termsSearchInputValue?.split(',')?.filter(item => item.trim() === cluster.title).length > 0}
              onClick={() => handleOnClusterClick(cluster.title)}>{`#${cluster.title} - ${cluster.focusTerms?.length}`}
            </SingleClusterContainer>
          </Tooltip>;
        })}
      </ClustersContainer>}
      <FontAwesomeIcon icon={isClustersExpanded ? faChevronUp : faChevronDown} style={{cursor: 'pointer', marginLeft: 'auto'}} onClick={() => setIsClustersExpanded(!isClustersExpanded)}/>
    </ClustersWrapper>
  </Wrapper>;
});

const ClusterTooltipSingleRow = styled.div`
  display: flex;
  justify-content: space-between
`;
const ClusterTooltipLabel = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: #E8E8E8;
`;

const ClusterTooltipValue = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #FFFFFF;
`;

const Wrapper = styled.div`
  margin-bottom: 15px;
  border-radius: 8px;
  background: #282B2F;
  box-shadow: 0px 4px 21px 0px rgba(0, 0, 0, 0.02);
  padding: 10px;
`;
const SearchAndSort = styled.div`
  display: flex;
  margin-bottom: 10px;
`;
const ClustersWrapper = styled.div`
  display: flex;
`;
const ClustersContainer = styled.div<{expandedClusters?: boolean}>`
  display: flex;
  flex-wrap: wrap;

  height: ${p => p.expandedClusters ? 'auto' : '25px'};
  overflow: hidden;
`;

const SingleClusterContainer = styled.div<{activeCluster?: boolean}>`
  background-color: ${p => p.activeCluster ? '#f8a62933' : '#2E3135'};
  border-radius: 60px;
  padding: 3px 8px;
  width: auto;
  height: 20px;
  font-size: 11px;
  margin-right: 6px;
  margin-bottom: 6px;
  cursor: pointer;

  &:hover {
    background-color: #4B4D51 !important;
  }
`;

const InputStyled = styled(AntdInput)`
  height: 32px;
  margin-bottom: 5px;
  background: #282B2F;
  border-color: transparent !important;
  .ant-input {
    margin-left: 7px;
    background: #282B2F;
    color: white !important;
    color: var(--Text-gray, var(--Gray, #A3A4A4));
    font-family: Inter;
    font-size: 13px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
  }
  .ant-input-affix-wrapper {
    border: none !important;
  }
  &:hover{
    box-shadow: none !important;
    border-color: transparent !important;
  }
  &:focus{
    box-shadow: none !important;
  }
`;
